import startCase from 'lodash/startCase';

import { TprmRequirementCategory, TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
import { DocumentDataReviewDetails } from '@evidentid/tprm-portal-lib/models/activity-history';

/**
 * What the reviewer was looking at, named as the customer thinks of it: a document.
 *
 * The backend sends the requirements a review covers, not a document type, because a review is scoped to
 * requirements rather than to files. The mapping back to a document is the bundling rule the backend already
 * applies: every insurance-category requirement goes into one review, precisely because they all come off a
 * single certificate of insurance. So an insurance review is a COI, and any other review names its one
 * requirement.
 *
 * Falls back to title-casing the raw coverage type when a requirement is not in the definitions map - which
 * happens for a requirement this tenant no longer has assigned, and for one the backend added before this app
 * was rebuilt.
 */
export function documentTypeForReview(
    details: DocumentDataReviewDetails | null | undefined,
    requirementDefinitions: Record<string, TprmRequirementDefinition> = {},
): string | undefined {
    const requirements = details?.requirements ?? [];
    if (!requirements.length) {
        return undefined;
    }
    const isInsuranceReview = requirements.some(
        ({ requirement }) => requirementDefinitions[requirement]?.category === TprmRequirementCategory.insurance,
    );
    if (isInsuranceReview) {
        return 'Certificate of Insurance';
    }
    const [ { requirement } ] = requirements;
    return requirementDefinitions[requirement]?.defaultLabel || startCase(requirement.toLowerCase());
}

/**
 * Human-readable summary for a DOCUMENT_DATA_REVIEW_COMPLETED entry, e.g.
 * "Document Data Review Completed: Certificate of Insurance".
 *
 * Completion only: product ruled out recording that a review was raised (PRODUCT-26060, 2026-08-31), since
 * the History tab records what a client decided and the Actions menu badge already flags waiting work.
 *
 * "Document Data Review" adopts the term customers already meet on the Actions tab and in the review modal.
 * A shorter name here would have left customers reading two names for one thing.
 *
 * Degrades to the bare stage when the document type cannot be determined, because "a review started" is still
 * true and useful, whereas guessing at which document would not be.
 */
export function formatDocumentDataReviewSummary(
    details: DocumentDataReviewDetails | null | undefined,
    requirementDefinitions: Record<string, TprmRequirementDefinition> = {},
): string {
    const documentType = documentTypeForReview(details, requirementDefinitions);
    return documentType
        ? `Document Data Review Completed: ${documentType}`
        : 'Document Data Review Completed';
}
